import { useState } from 'react'
import { ChevronDown, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { PackagingItem } from '@/shared/hooks/api/packagingSuppliers'
import type { AddPackagingItemTarget } from './SupplierDetailPackagingCatalog'
import { LexAssignPackagingItemSheet } from './LexAssignPackagingItemSheet'

type LexAddPackagingToPartMenuProps = {
  supplierId: string
  packagedItemId: string
  packedItemSku: string
  assignedPackagingItemIds: ReadonlySet<string>
  packagingItems: PackagingItem[]
  hasSupplierAddress: boolean
  onAddItem: (target: Extract<AddPackagingItemTarget, { type: 'packedItem' }>) => void
}

export function LexAddPackagingToPartMenu({
  supplierId,
  packagedItemId,
  packedItemSku,
  assignedPackagingItemIds,
  packagingItems,
  hasSupplierAddress,
  onAddItem,
}: LexAddPackagingToPartMenuProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [assignSheetOpen, setAssignSheetOpen] = useState(false)

  if (!hasSupplierAddress) {
    return (
      <Button type='button' variant='outline' size='xs' disabled>
        <Plus className='size-3' />
        {t('details.partPackaging.addOrAssignPackaging')}
      </Button>
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type='button' variant='outline' size='xs'>
            <Plus className='size-3' />
            {t('details.partPackaging.addOrAssignPackaging')}
            <ChevronDown className='size-3 opacity-70' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem
            onSelect={() => {
              onAddItem({
                type: 'packedItem',
                packagedItemId,
                packedItemSku,
              })
            }}
          >
            {t('details.packagingCatalog.addForPackedItem')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setAssignSheetOpen(true)}>
            {t('details.packagingCatalog.assignPackagingItemOption')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <LexAssignPackagingItemSheet
        open={assignSheetOpen}
        onOpenChange={setAssignSheetOpen}
        supplierId={supplierId}
        packagedItemId={packagedItemId}
        assignedPackagingItemIds={assignedPackagingItemIds}
        packagingItems={packagingItems}
      />
    </>
  )
}
